import querystring from 'query-string'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { axiosFetch, isAxiosCancellation } from '@halo/_utils/axios'
import { Singleton, Container } from 'typescript-ioc/es5'
import { simpleNotification } from '@halo/_utils/notifications'
import aqe from '@pushly/aqe'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { SegmentDto } from '@halo/dtos/segment.dto'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'

export type EstimateReturnType = 'count' | 'count_by_channel'
export type RequiredAudienceType = 'NOTIFICATION' | 'APP_MESSAGE'

@Singleton
export class SegmentService {
    private appState: AppState
    private appService: AppService

    public constructor() {
        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public async fetchSegmentsByDomainId(
        domainId: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<SegmentDto[]>> {
        let ok = false
        let data: SegmentDto[] = []
        let meta: any = {}

        if (opts.showLoadingScreen) this.appService.setModuleLoading()
        const qsOpts = querystring.stringify(opts.query ?? {})

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/v3/domains/${domainId}/segments?${qsOpts}`,
                },
                opts.cancellationKey,
            )
            const { data: segments, ...resMeta } = res.data

            ok = true
            data = segments.map(SegmentDto.parse)
            meta = resMeta
        } catch (error) {
            if (!isAxiosCancellation(error) && !!error?.response?.data?.message) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) this.appService.unsetModuleLoading()

        return { ok, data, meta }
    }

    public async estimateSegmentReach(
        domainId: number,
        segmentIds: number[],
        excludedSegmentIds: number[],
        targetedChannels: DeliveryChannel[],
        returnType: EstimateReturnType,
        requiredAudienceTypes: RequiredAudienceType[],
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<number | undefined>> {
        let ok = false
        let cancelled = false
        let estimate: number | undefined

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/segments/estimate-reach`,
                    data: {
                        segment_ids: segmentIds,
                        excluded_segment_ids: excludedSegmentIds,
                        targeted_channels: targetedChannels,
                        return_type: returnType,
                        required_audience_types: requiredAudienceTypes,
                    },
                },
                opts.cancellationKey,
            )

            ok = true
            estimate = req.data.data?.reach ?? 0
        } catch (error) {
            if (!isAxiosCancellation(error)) {
                simpleNotification('error', error.response.data.message)
            } else {
                cancelled = true
            }
        }

        return { ok, data: estimate, cancelled }
    }
}
